import React from 'react';

import { FormattedMessage } from 'react-intl';
import { createSelector } from 'reselect';

import { getCarrierDisplayName } from '../../components/data-driven-logo/get-carrier-display-name';
import { getMarketplaceDisplayName } from '../../components/data-driven-logo/get-marketplace-display-name';
import { getProviderDisplayName } from '../../components/data-driven-logo/get-provider-display-name';
import { emptyArray } from 'ui-components/default-props/empty';
import { PackageService } from '../../services/package-service';
import Global from '../../store/global-selectors';
import { BRAND_FILTER_ID } from '../orders-filter-bar-container/filter-options/options/seller-brand';
import { messages } from './messages';

export const stringFilterOperators = {
  isBlank: 'isBlank',
  isNotBlank: 'isNotBlank',
  equals: 'equals',
  doesNotEqual: 'doesNotEqual',
  startsWith: 'startsWith',
  doesNotStartWith: 'doesNotStartWith',
  endsWith: 'endsWith',
  doesNotEndWith: 'doesNotEndWith',
  contains: 'contains',
  doesNotContain: 'doesNotContain',
};

export const dateFilterOperators = {
  neverModified: 'neverModified',
  dateToday: 'dateToday',
  dateYesterday: 'dateYesterday',
  dateLastWeek: 'dateLastWeek',
  dateLast30Days: 'dateLast30Days',
  dateOnOrBefore: 'dateOnOrBefore',
  dateOnOrAfter: 'dateOnOrAfter',
  dateThisMonth: 'dateThisMonth',
  dateLastMonth: 'dateLastMonth',
  dateCustomRange: 'dateCustomRange',
  dateWithinThePastXDays: 'dateWithinThePastXDays',
  dateWithinTheNextXDays: 'dateWithinTheNextXDays',
  dateXDaysAgoOrMore: 'dateXDaysAgoOrMore',
  dateXDaysAheadOrMore: 'dateXDaysAheadOrMore',
};

const buildCriterionArgumentObject = (id, displayName, value) => {
  return {
    id,
    displayName,
    value,
  };
};

const buildFilterArguments = (argumentRefs, optionString, idFieldName) => {
  return argumentRefs.map((argRef) => buildCriterionArgumentObject(optionString, argRef.name, argRef[idFieldName]));
};

const concatNoneCriterionArgumentObject = (list, optionName) => {
  const none = [
    {
      id: optionName,
      displayName: <FormattedMessage {...messages.unassigned} />,
      value: '00000000-0000-0000-0000-000000000000',
    },
  ];
  return none.concat(list);
};

const buildUniqueArgumentsList = (args, idName) => {
  return args.reduce((uniqueArgsList, arg) => {
    if (uniqueArgsList.some((uniqueArg) => uniqueArg[idName] === arg[idName])) {
      return uniqueArgsList;
    }
    uniqueArgsList.push(arg);
    return uniqueArgsList;
  }, []);
};

const filteredDownPackagesSelector = createSelector(
  (state) => Global.selectSellerPackages(state) || emptyArray,
  (packages) => PackageService.removeBadPackages(packages),
);

export const getUniquePackageTypeList = (packages) => {
  const map = {};
  return packages.reduce((result, current) => {
    if (!map[current.packageTypeId]) {
      map[current.packageTypeId] = true;
      result.push({ ...current });
    }
    return result;
  }, []);
};

export const filterUniquePackageOptions = createSelector(filteredDownPackagesSelector, (packages) => {
  return getUniquePackageTypeList(packages);
});

const selectSellerServicesSelector = createSelector(
  (state) => Global.selectSellerServices(state),
  (sellerServices) => sellerServices.filter((service) => !service.isHidden),
);

export const serviceCollectionsNameCorrection = ({ intl, serviceCollections = emptyArray }) => {
  const map = {};
  const results = serviceCollections.map((collection) => {
    if (collection.isActive) {
      return collection;
    }

    const isInMap = !!map[collection.displayName];
    // oxlint-disable-next-line no-param-reassign, no-unused-expressions
    isInMap ? map[collection.displayName]++ : (map[collection.displayName] = 1);
    const displayName =
      isInMap && map[collection.displayName] > 1
        ? `${collection.displayName} (${intl.formatMessage(messages.deleted)}-${map[collection.displayName]})`
        : `${collection.displayName} (${intl.formatMessage(messages.deleted)})`;
    return { ...collection, displayName };
  });

  return results.sort((a, b) => {
    if (a.displayName === b.displayName) {
      return 0;
    }
    if (a.displayName > b.displayName) {
      return 1;
    }
    return -1;
  });
};

export const getReferenceDataCriteriaArguments = createSelector(
  (state) => Global.selectSellerActiveStores(state) || emptyArray,
  (state) => Global.selectSellerUsersSortedByName(state) || emptyArray,
  (state) => Global.selectReferenceDatagetSellerCarriers(state) || emptyArray,
  (state) => Global.selectReferenceDatagetMarketplaces(state) || emptyArray,
  filterUniquePackageOptions,
  (state) => Global.selectSellerShipFroms(state) || emptyArray,
  selectSellerServicesSelector,
  (state) => Global.selectSellerServiceCollections(state) || emptyArray,
  (state) => Global.selectSellerLabelProviders(state) || emptyArray,
  (state) => Global.selectSellerTags(state) || emptyArray,
  (state) => Global.selectReferenceDatagetProviders(state) || emptyArray,
  (state) => Global.selectReferenceDatagetCountries(state),
  (state) => Global.selectReferenceDatagetStates(state),
  (state) => Global.selectEntitiesProductCategories(state),
  (state) => Global.selectEntitiesProductProfiles(state),
  (state) => Global.selectSellerSellerBrands(state),
  (
    stores,
    users,
    carriers,
    marketplaces,
    filteredDownPackages,
    shipFroms,
    services,
    serviceCollections,
    shippingAccounts,
    tags,
    providers,
    countries,
    states,
    prodCategories,
    prodProfiles,
    sellerBrands,
  ) => {
    const uniqueServices = buildUniqueArgumentsList(services, 'serviceId');
    const countryFilterArguments = buildFilterArguments(countries, 'option-country', 'code');
    const assignToFilterArguments = buildFilterArguments(users, 'option-assignedTo', 'userId');
    const productCategories = buildFilterArguments(prodCategories, 'option-category', 'categoryId');
    const productProfiles = buildFilterArguments(Object.values(prodProfiles), 'option-profile', 'productProfileId');
    const mappedSellerBrands = sellerBrands.map((sellerBrand) => ({
      ...sellerBrand,
      name: sellerBrand.companyName,
    }));

    const mappedCarriers = buildFilterArguments(carriers, 'option-carrier', 'carrierId').map((carrier) => ({
      ...carrier,
      displayName: <FormattedMessage {...getCarrierDisplayName(carrier.value)} />,
    }));

    const mappedMarketplaces = buildFilterArguments(marketplaces, 'option-marketplace', 'marketplaceId').map((marketplace) => ({
      ...marketplace,
      displayName: <FormattedMessage {...getMarketplaceDisplayName(marketplace.value)} />,
    }));

    const mappedShippingAccounts = shippingAccounts.map((account) => {
      const nickname = account.useHouseCredentials ? '' : account.name;
      const providerName = getProviderDisplayName(account.labelProviderId)?.defaultMessage;
      const displayName = nickname ? `${providerName} - (${nickname})` : providerName;

      return {
        ...buildCriterionArgumentObject('option-account', account.name, account.sellerLabelProviderId),
        displayName,
      };
    });

    const stateFilterArguments = states.map((state) => {
      const displayName = state.name;

      return {
        ...buildCriterionArgumentObject('option-state', displayName, `${state.countryCode}-${state.code}`),
        displayName,
      };
    });

    const serviceCollection = serviceCollections.map((collection) => ({
      ...buildCriterionArgumentObject('option-service-collections', collection.name, collection.serviceCollectionGuid),
      isActive: collection.isActive,
    }));

    return {
      store: buildFilterArguments(stores, 'option-store', 'storeGuid'),
      assignedTo: concatNoneCriterionArgumentObject(assignToFilterArguments, 'option-assignedTo'),
      carrier: mappedCarriers,
      marketplace: mappedMarketplaces,
      package: buildFilterArguments(filteredDownPackages, 'option-packageType', 'packageTypeId'),
      shipFrom: buildFilterArguments(shipFroms, 'option-shipFrom', 'shipFromId'),
      returnsShipTo: buildFilterArguments(shipFroms, 'option-returnsShipTo', 'shipFromId'),
      shippingAccount: mappedShippingAccounts,
      shipmentsShippingAccount: mappedShippingAccounts,
      requestedServiceMapped: buildFilterArguments(uniqueServices, 'option-service', 'serviceId'),
      service: buildFilterArguments(uniqueServices, 'option-service', 'serviceId'),
      serviceCollection,
      tag: tags,
      provider: buildFilterArguments(providers, 'option-provider', 'labelProviderId'),
      country: countryFilterArguments,
      returnsCountry: countryFilterArguments,
      state: stateFilterArguments,
      returnsState: stateFilterArguments,
      marketplaceUsernames: marketplaces,
      productCategories,
      productProfiles,
      [BRAND_FILTER_ID]: buildFilterArguments(mappedSellerBrands, 'option-companyName', 'sellerBrandId'),
    };
  },
);
